import {
  COUNTRY_OF_EXPORT_MISSING,
  GROSS_WEIGHT_MISSING,
  HOUSE_AIR_BILL_NUMBER_MISSING,
  HOUSE_AIR_BILL_NUMBER_TOO_LONG,
  HOUSE_AIR_BILL_PREFIX_MISSING,
  HOUSE_AIR_BILL_PREFIX_WRONG_LENGTH,
  HOUSE_BILL_CARRIER_MISSING,
  HOUSE_BILL_CARRIER_WRONG_LENGTH,
  HOUSE_BILL_NUMBER_MISSING,
  HOUSE_BILL_TOO_LONG,
  HTS_COUNTRY_OF_ORIGIN_INVALID,
  HTS_COUNTRY_OF_ORIGIN_MISSING,
  HTS_DOES_NOT_EXIST,
  HTS_NUMBER_MISSING,
  HTS_NUMBER_WRONG_LENGTH,
  INVOICE_MANUFACTURER_MISSING,
  INVOICE_NUMBER_MALFORMED,
  INVOICE_NUMBER_MISSING,
  MASTER_AIR_BILL_NUMBER_MISSING,
  MASTER_AIR_BILL_NUMBER_TOO_LONG,
  MASTER_AIR_BILL_PREFIX_MISSING,
  MASTER_AIR_BILL_PREFIX_WRONG_LENGTH,
  MASTER_BILL_CARRIER_MISSING,
  MASTER_BILL_CARRIER_WRONG_LENGTH,
  MASTER_BILL_NUMBER_MISSING,
  MASTER_BILL_TOO_LONG,
  QUANTITY1_MISSING,
  QUANTITY2_MALFORMED,
  QUANTITY3_MALFORMED,
  SPECIAL_CHAPTER_HTS_DOES_NOT_EXIST,
  SPECIAL_CHAPTER_MISSING,
  STATE_OF_ORIGIN_INVALID,
  STATE_OF_ORIGIN_MISSING,
  TRIP_MISSING,
  UNIT1_MISSING,
  VALUE_MISSING,
} from '@constants/entity-validation-messages';
import { cloneDeep, omit, union } from 'lodash-es';
import { ValidationMessageModel } from '@data-layer/models/common';
import { InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { EntityModel } from '@data-layer/models/entities';
import { UtilsHelper } from '@helpers/utils-helper';

export class EntrySummaryValidationHelper {
  public static get invoiceMapValidation() {
    const map = new Map<string, (invoice: InvoiceModel) => string[]>();

    map.set('invoiceNumber', this.invoiceNumberValidationFc.bind(this));
    map.set('currency', this.invoiceCurrencyValidationFc.bind(this));
    map.set('incoTermsCode', this.invoiceIncoTermsCodeValidationFc.bind(this));
    map.set('manufacturerId', this.invoiceManufacturerValidationFc.bind(this));

    return map;
  }

  public static get itemMapValidation() {
    const map = new Map<string, (item: LineModel) => string[]>();

    map.set('countryOfOrigin', this.itemCountryOfOriginValidationFc.bind(this));
    map.set('stateOfOrigin', this.itemStateOfOriginValidationFc.bind(this));
    map.set('countryOfExport', this.itemCountryOfExportValidationFc.bind(this));
    map.set('grossWeight', this.itemGrossWeightCodeValidationFc.bind(this));
    map.set('htsNumber', this.itemHtsNumberValidationFc.bind(this));
    map.set('quantity1', this.itemQuantity1ValidationFc.bind(this));
    map.set('quantity2', this.itemQuantity2ValidationFc.bind(this));
    map.set('quantity3', this.itemQuantity3ValidationFc.bind(this));
    map.set('specialChapterHtsNumber', this.itemSpecialChapterHtsNumberValidationFc.bind(this));
    map.set('unitOfMeasure1', this.itemUnitOfMeasure1ValidationFc.bind(this));
    map.set('value', this.itemValueValidationFc.bind(this));

    return map;
  }

  public static invoiceNumberValidationFc(invoice: InvoiceModel) {
    return this.findErrors(invoice, [INVOICE_NUMBER_MISSING, INVOICE_NUMBER_MALFORMED]);
  }

  public static invoiceCurrencyValidationFc(invoice: InvoiceModel) {
    const messages: string[] = [];
    return UtilsHelper.uniq<string>(messages);
  }

  public static invoiceIncoTermsCodeValidationFc(invoice: InvoiceModel) {
    const messages: string[] = [];
    return UtilsHelper.uniq<string>(messages);
  }

  public static invoiceManufacturerValidationFc(invoice: InvoiceModel) {
    return this.findErrors(invoice, [INVOICE_MANUFACTURER_MISSING]);
  }

  public static itemCountryOfOriginValidationFc(item: LineModel) {
    return this.findErrors(item, [HTS_COUNTRY_OF_ORIGIN_MISSING, HTS_COUNTRY_OF_ORIGIN_INVALID]);
  }

  public static itemStateOfOriginValidationFc(item: LineModel) {
    return this.findErrors(item, [STATE_OF_ORIGIN_MISSING, STATE_OF_ORIGIN_INVALID]);
  }

  public static itemCountryOfExportValidationFc(item: LineModel) {
    return this.findErrors(item, [COUNTRY_OF_EXPORT_MISSING]);
  }

  public static itemGrossWeightCodeValidationFc(item: LineModel) {
    return this.findErrors(item, [GROSS_WEIGHT_MISSING]);
  }

  public static itemHtsNumberValidationFc(item: LineModel) {
    return this.findErrors(item, [HTS_NUMBER_MISSING, HTS_NUMBER_WRONG_LENGTH, HTS_DOES_NOT_EXIST]);
  }

  public static itemQuantity1ValidationFc(item: LineModel) {
    return this.findErrors(item, [QUANTITY1_MISSING]);
  }

  public static itemQuantity2ValidationFc(item: LineModel) {
    return this.findErrors(item, [QUANTITY2_MALFORMED]);
  }

  public static itemQuantity3ValidationFc(item: LineModel) {
    return this.findErrors(item, [QUANTITY3_MALFORMED]);
  }

  public static itemSpecialChapterHtsNumberValidationFc(item: LineModel) {
    return this.findErrors(item, [SPECIAL_CHAPTER_MISSING, SPECIAL_CHAPTER_HTS_DOES_NOT_EXIST]);
  }

  public static itemUnitOfMeasure1ValidationFc(item: LineModel) {
    return this.findErrors(item, [UNIT1_MISSING]);
  }

  public static itemValueValidationFc(item: LineModel) {
    return this.findErrors(item, [VALUE_MISSING]);
  }

  public static getImporterValidationMessages(importer: EntityModel, transportationMode: enumTransportationMode): ValidationMessageModel[] {
    return this.prepareErrors(importer.validationMessages, transportationMode);
  }

  public static getValidationMessages(
    obj: { validationMessages: ValidationMessageModel[] },
    transportationMode: enumTransportationMode,
    onlySelf: boolean = false,
    key: string[] = [],
    joinKeys: boolean = false,
  ): ValidationMessageModel[] {
    let messages: ValidationMessageModel[] = [];
    if (!obj) {
      return [];
    }
    const preparedMessages: ValidationMessageModel[] = this.prepareErrors(this.toArray(obj.validationMessages), transportationMode);
    if (preparedMessages.length) {
      const messagesToConcat: ValidationMessageModel[] = joinKeys ? this.joinKeysMessages(preparedMessages, key) : preparedMessages;
      messages = union<ValidationMessageModel>(messages, messagesToConcat);
    }
    if (onlySelf) {
      return messages;
    }
    for (const field in omit(obj, ['validationMessages'])) {
      if (obj[field]) {
        const newKeys = union(key, [field]).map((item) => item.toUpperCase());
        if (Array.isArray(obj[field])) {
          for (const item of obj[field]) {
            messages = union(messages, this.getValidationMessages(item, transportationMode, false, newKeys, joinKeys));
          }
        } else if (typeof obj[field] === 'object') {
          messages = union(messages, this.getValidationMessages(obj[field], transportationMode, false, newKeys, joinKeys));
        }
      }
    }
    return UtilsHelper.uniqBy<ValidationMessageModel>(messages, 'text');
  }

  public static replaceAirTextError(text: string): string {
    return text.replace('AIR_', '');
  }

  private static toArray<T>(obj: T[] | { [key: string]: T }): T[] {
    if (!obj) {
      return [];
    }
    if (Array.isArray(obj)) {
      return obj;
    } else if (UtilsHelper.isObject(obj) && Object.keys(obj).length > 0) {
      return Object.keys(obj).map((field: string) => obj[field]);
    }
    return [];
  }

  private static joinKeysMessages(messages: ValidationMessageModel[], keys: string[]) {
    const newKeys = cloneDeep(keys).map((item) => item.toUpperCase());
    return cloneDeep(messages).map((item) => {
      item.text = UtilsHelper.concat<string>(newKeys, item.text).join('.');
      return item;
    });
  }

  private static prepareErrors(messages: ValidationMessageModel[], transportationMode: enumTransportationMode): ValidationMessageModel[] {
    return cloneDeep(messages).map((item) =>
      transportationMode === enumTransportationMode.AIR ? this.replaceAirError(item) : cloneDeep(item),
    );
  }

  private static replaceAirError(item: ValidationMessageModel): ValidationMessageModel {
    const map: Map<string, string> = new Map([
      [TRIP_MISSING, 'FLIGHT_NUMBER_MISSING'],
      [MASTER_BILL_CARRIER_MISSING, MASTER_AIR_BILL_PREFIX_MISSING],
      [MASTER_BILL_NUMBER_MISSING, MASTER_AIR_BILL_NUMBER_MISSING],
      [MASTER_BILL_CARRIER_WRONG_LENGTH, MASTER_AIR_BILL_PREFIX_WRONG_LENGTH],
      [MASTER_BILL_TOO_LONG, MASTER_AIR_BILL_NUMBER_TOO_LONG],
      [HOUSE_BILL_CARRIER_MISSING, HOUSE_AIR_BILL_PREFIX_MISSING],
      [HOUSE_BILL_NUMBER_MISSING, HOUSE_AIR_BILL_NUMBER_MISSING],
      [HOUSE_BILL_CARRIER_WRONG_LENGTH, HOUSE_AIR_BILL_PREFIX_WRONG_LENGTH],
      [HOUSE_BILL_TOO_LONG, HOUSE_AIR_BILL_NUMBER_TOO_LONG],
    ]);
    if (map.has(item.text)) {
      return { ...item, text: map.get(item.text) };
    }
    return item;
  }

  private static findErrors(object: { validationMessages: ValidationMessageModel[]; [key: string]: any }, errors: string[]): string[] {
    const messages: string[] = [];

    for (const error of errors) {
      if (object && object.validationMessages && object.validationMessages.find((msg) => msg.text === error)) {
        messages.push(error);
      }
    }

    return UtilsHelper.uniq<string>(messages);
  }
}
